import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import DSDialog from "../design-system/DSDialog";
import Icon from "../Icon";
import ConversionSteps, { type ConversionStep } from "./ConversionSteps";
import {
  conversionCheckPresentation,
  type ConversionCheckTone,
} from "../../utils/conversionCheck";
import designTokens, {
  colors,
  numbers,
  typography,
} from "../../styles/designTokens";
import type { ConversionReportResultSummary } from "../../store/uploadStore";

export interface ConversionReportModalProps {
  open: boolean;
  result: ConversionReportResultSummary | null;
  onClose: () => void;
  onContactSupport: (result: ConversionReportResultSummary) => void;
}

const EVENT_LABELS: Record<string, string> = {
  DEFAULT_MATERIAL_APPLIED:
    "The source model has no material, so a default gray material was applied.",
  EXTERNAL_TEXTURE_MISSING:
    "A referenced texture was not found in the uploaded package.",
  LOADER_VSE_READ_FAILED: "The converted object file could not be read.",
  LOADER_GLB_LOAD_FAILED: "The converted geometry could not be loaded.",
  LOADER_SCENE_CREATE_FAILED: "The object could not be created in the scene.",
  LOADER_UNHANDLED_EXCEPTION: "The object stopped loading unexpectedly.",
  LOADER_MATERIAL_MAPPING_FAILED: "Some materials could not be applied.",
  LOADER_MATERIAL_SLOT_MISMATCH:
    "Some material assignments may differ from the original.",
  LOADER_PBR_MAPPING_MISMATCH:
    "Some surface properties may differ from the original.",
  LOADER_TEXTURE_MAPPING_MISMATCH:
    "Some textures may differ from the original.",
  LOADER_UV_MAPPING_MISMATCH:
    "Some texture placement may differ from the original.",
  LOADER_TRANSPARENCY_MISMATCH:
    "Some transparency may differ from the original.",
  LOADER_DOUBLE_SIDED_MISMATCH:
    "Some surface visibility may differ from the original.",
};

const HIDDEN_USER_EVENT_CODES = new Set([
  "LOADER_MATERIAL_METADATA_MISSING",
  "LOADER_MATERIAL_VALIDATION_FAILED",
]);

const checkTokens = designTokens.semantic.libraryDetailDrawer.conversionCheck;

const checkRowColor = (tone: ConversionCheckTone): string => {
  if (tone === "needs_review") return checkTokens.warning;
  if (tone === "failed") return colors.red[50].dark;
  if (tone === "not_checked") return checkTokens.notCheckedValue;
  return colors.primary[50].dark;
};

const FAILURE_STAGES = [
  { key: "uploading", label: "Uploading" },
  { key: "converting", label: "Converting" },
  { key: "optimizing", label: "Optimizing" },
  { key: "rendering", label: "Rendering" },
  { key: "complete", label: "Complete" },
] as const;

type FailureStageKey = (typeof FAILURE_STAGES)[number]["key"];

const failureStage = (
  result: ConversionReportResultSummary,
): FailureStageKey => {
  if (!("source" in result)) return "rendering";
  if (result.stage === "rendering") return "rendering";
  if (result.stage === "uploading") return "uploading";
  if (result.stage === "optimizing") return "optimizing";
  return "converting";
};

const resultIssues = (result: ConversionReportResultSummary) => {
  if ("eventCodes" in result) {
    return Array.from(
      new Set(
        result.eventCodes
          .filter(
            (code) =>
              !code.endsWith("_SUCCESS") && !HIDDEN_USER_EVENT_CODES.has(code),
          )
          .map(
            (code) => EVENT_LABELS[code] ?? "A validation check needs review.",
          ),
      ),
    );
  }
  if (result.stage === "uploading") {
    return ["The original file could not be uploaded."];
  }
  if (result.stage === "optimizing") {
    return ["The object could not be optimized."];
  }
  if (result.stage === "rendering") {
    return ["The object could not be displayed in the editor."];
  }
  return ["The CAD geometry could not be converted into a mesh."];
};

const secondaryButtonSx = (minWidth: number) => ({
  height: 48,
  minWidth,
  px: numbers.gap[12],
  color: colors.grey[80].dark,
  borderColor: colors.grey[60].dark,
  borderRadius: numbers.cornerRadius[8],
  textTransform: "none" as const,
  boxShadow: "none",
  ...typography.styles.viewrStudio.bodyRegular,
});

const primaryButtonSx = (minWidth: number) => ({
  height: 48,
  minWidth,
  px: numbers.gap[12],
  bgcolor: colors.primary[50].dark,
  color: colors.common[100].dark,
  borderRadius: numbers.cornerRadius[8],
  textTransform: "none" as const,
  boxShadow: "none",
  ...typography.styles.viewrStudio.bodyRegular,
  "&:hover": {
    bgcolor:
      designTokens.semantic.button.primaryAction.background.hover.dark,
    boxShadow: "none",
  },
});

const ConversionReportModal: React.FC<ConversionReportModalProps> = ({
  open,
  result,
  onClose,
  onContactSupport,
}) => {
  if (!result) return null;

  const reportTokens = designTokens.semantic.conversionReport;
  const fileName = "fileName" in result ? result.fileName : null;
  const isFailed = result.status === "failed";
  const issues = resultIssues(result);
  const activeStageKey = failureStage(result);
  const activeStageIndex = FAILURE_STAGES.findIndex(
    (stage) => stage.key === activeStageKey,
  );
  const projection = "eventCodes" in result ? result.projection : null;
  const checkRows = isFailed ? [] : conversionCheckPresentation(projection).rows;

  const failureSteps: ConversionStep[] = FAILURE_STAGES.map((stage, index) => {
    const isFailedStage = index === activeStageIndex;
    const isCompletedStage = index < activeStageIndex;
    return {
      key: stage.key,
      label: stage.label,
      state: isFailedStage
        ? "failed"
        : isCompletedStage
          ? "complete"
          : "pending",
      labelColor: isFailedStage ? colors.red[50].dark : undefined,
      trailing: isFailedStage ? (
        <Typography
          sx={{
            ...typography.styles.viewrStudio.captionRegular,
            color: colors.red[50].dark,
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          Stopped here
        </Typography>
      ) : undefined,
      detail:
        isFailedStage && issues.length > 0 ? (
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: numbers.gap[4] }}
          >
            {issues.map((issue) => (
              <Typography
                key={issue}
                sx={{
                  ...typography.styles.viewrStudio.label2Regular,
                  color: colors.grey[90].dark,
                }}
              >
                {issue}
              </Typography>
            ))}
          </Box>
        ) : undefined,
    };
  });

  return (
    <DSDialog
      open={open}
      onClose={onClose}
      size="md"
      aria-labelledby="conversion-report-title"
      paperSx={{ width: reportTokens.width }}
    >
      <Box aria-hidden="true" sx={{ width: "100%", height: numbers.gap[16] }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: numbers.gap[64],
          px: numbers.gap[32],
          gap: numbers.gap[8],
        }}
      >
        {isFailed && (
          <Icon
            name="error-solid-24"
            aria-hidden="true"
            style={{ color: designTokens.fill.icon.yellow50.dark }}
          />
        )}
        <Typography
          id="conversion-report-title"
          sx={{
            ...typography.styles.viewrStudio.headingMedium,
            color: colors.grey[90].dark,
            flex: 1,
            minWidth: 0,
          }}
        >
          Conversion report
        </Typography>
        <IconButton
          aria-label="Close conversion report"
          onClick={onClose}
          sx={{
            width: 24,
            height: 24,
            p: 0,
            color: colors.grey[60].dark,
            "&:hover": { bgcolor: "transparent" },
          }}
        >
          <Icon name="close-24" aria-hidden="true" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: isFailed ? reportTokens.bodyGap : numbers.gap[16],
          px: numbers.gap[32],
          pt: isFailed ? "6px" : numbers.gap[8],
          pb: isFailed ? numbers.gap[16] : numbers.gap[8],
        }}
      >
        <Typography
          sx={{
            ...(isFailed
              ? typography.styles.viewrStudio.bodyRegular
              : typography.styles.viewrStudio.headline1Regular),
            color: isFailed ? colors.grey[80].dark : colors.grey[90].dark,
          }}
        >
          {isFailed
            ? "This object was not added to Library."
            : "Everything else came through. Review the items below."}
        </Typography>
        {isFailed ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: numbers.gap[12],
              minWidth: 0,
            }}
          >
            <Icon
              name="3d-object-16"
              aria-hidden="true"
              style={{ color: colors.grey[90].dark }}
            />
            <Typography
              data-testid="conversion-report-filename"
              sx={{
                ...typography.styles.viewrStudio.bodyRegular,
                fontWeight: 500,
                color: colors.common[100].dark,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName || "Object"}
            </Typography>
            <Box
              sx={{
                flexShrink: 0,
                px: "8px",
                py: "3px",
                borderRadius: "20px",
                bgcolor: reportTokens.statusChip.failedBackground,
                border: `1px solid ${reportTokens.statusChip.failedBorder}`,
              }}
            >
              <Typography
                sx={{
                  ...typography.styles.viewrStudio.captionRegular,
                  color: colors.red[50].dark,
                  fontWeight: 500,
                }}
              >
                Failed
              </Typography>
            </Box>
          </Box>
        ) : (
          // 시안은 오브젝트 이름과 문제 행을 카드 하나에 담는다. 이름이 그룹의
          // 머리이고 행이 그 내용이다 (View R UI `4146:76692`).
          <Box
            data-testid="conversion-report-checklist"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              px: numbers.gap[16],
              pt: numbers.gap[16],
              pb: "20px",
              borderRadius: reportTokens.warning.cardRadius,
              bgcolor: colors.grey[30].dark,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: numbers.gap[8],
                minWidth: 0,
              }}
            >
              <Icon
                name="3d-object-16"
                aria-hidden="true"
                style={{ color: designTokens.fill.icon.grey60.dark }}
              />
              <Typography
                data-testid="conversion-report-filename"
                sx={{
                  ...typography.styles.viewrStudio.label1Medium,
                  color: colors.grey[90].dark,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName || "Object"}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: numbers.gap[16] }}
            >
              {checkRows.map((row) => {
                const tone = row.tone;
                const color = checkRowColor(tone);
                return (
                  <Box
                    key={row.label}
                    data-check-row={row.label}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: numbers.gap[4],
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: numbers.gap[8],
                      }}
                    >
                      <Icon
                        name={tone === "not_checked" ? "info-16" : "error-solid-16"}
                        size={16}
                        aria-hidden="true"
                        style={{ color: tone === "needs_review" ? designTokens.fill.icon.yellow50.dark : color }}
                      />
                      <Typography
                        sx={{
                          ...typography.styles.viewrStudio.bodyRegular,
                          color: colors.grey[90].dark,
                        }}
                      >
                        {row.label}
                      </Typography>
                      {row.value && (
                        <Box
                          sx={{
                            px: "8px",
                            py: "2px",
                            borderRadius: numbers.cornerRadius[4],
                            bgcolor:
                              tone === "not_checked"
                                ? checkTokens.chip.notCheckedBackground
                                : reportTokens.warning.chipBackground,
                          }}
                        >
                          <Typography
                            sx={{
                              ...typography.styles.viewrStudio.label2Medium,
                              color,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.value}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {row.detail && (
                      <Typography
                        sx={{
                          ...typography.styles.viewrStudio.label2Regular,
                          color: colors.grey[60].dark,
                          pl: "24px",
                        }}
                      >
                        {row.detail}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {isFailed && (
          <ConversionSteps
            data-testid="conversion-report-steps"
            steps={failureSteps}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: numbers.gap[24],
          height: numbers.gap[64],
          px: numbers.gap[32],
        }}
      >
        {/*
          시안의 primary는 다음에 할 일이다. 경고는 읽고 닫으면 되므로 Close가
          primary이고, 실패는 지원 요청이 다음 단계라 Contact support가 primary다
          (View R UI `2918:358371` / `2903:98911`).
        */}
        <Button
          variant="outlined"
          onClick={isFailed ? onClose : () => onContactSupport(result)}
          sx={secondaryButtonSx(isFailed ? 100 : 140)}
        >
          {isFailed ? "Close" : "Contact support"}
        </Button>
        <Button
          variant="contained"
          onClick={isFailed ? () => onContactSupport(result) : onClose}
          sx={primaryButtonSx(isFailed ? 140 : 100)}
        >
          {isFailed ? "Contact support" : "Close"}
        </Button>
      </Box>

      <Box aria-hidden="true" sx={{ width: "100%", height: numbers.gap[16] }} />
    </DSDialog>
  );
};

export default ConversionReportModal;
